import { LazyCombobox } from "@/components/ui/lazy-combobox";
import { agentPickerSource, toAgentOption } from "@/services/assistant.query";
import { getAssistant } from "@/services/assistant.service";
import { isNotFound } from "@/lib/api-client";

interface AgentComboboxProps {
  workspaceId?: string;
  value: string | null;
  onChange: (agentId: string | null) => void;
  placeholder?: string;
  /** Label for the entry that clears the selection. Omit to require one. */
  clearLabel?: string;
  /** The selected agent's name, when the caller already has it. Saves a request. */
  selectedLabel?: string;
  emptyMessage?: string;
  /** Id of the trigger — what a visible `<Label htmlFor>` points at. */
  id?: string;
  /** Accessible name for the trigger, where no visible <Label> points at it. */
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * The agent picker, everywhere one is needed.
 *
 * It is `LazyCombobox` pointed at `POST /assistant/list` — the same endpoint
 * the Agents table pages through. Nothing is fetched until it is opened, the
 * search box filters server-side, and scrolling loads the next page: a
 * workspace with 400 agents costs a page of 20, not 400 rows per dialog on
 * screen.
 */
export function AgentCombobox({
  workspaceId,
  value,
  onChange,
  placeholder = "Select an agent",
  clearLabel,
  selectedLabel,
  emptyMessage = "No agents found.",
  id,
  ariaLabel,
  disabled,
  className,
}: AgentComboboxProps) {
  return (
    <LazyCombobox
      value={value}
      onChange={onChange}
      {...agentPickerSource(workspaceId)}
      // The agent a form opened with is on no page the dropdown has loaded, so
      // it is fetched by id. Only when the caller did not pass `selectedLabel`,
      // and only once per dialog. A 404 is an agent deleted since the form
      // loaded: null clears the selection instead of keeping a dead id.
      fetchSelected={async (id) => {
        try {
          return toAgentOption(await getAssistant(id));
        } catch (error) {
          if (isNotFound(error)) return null;
          throw error;
        }
      }}
      selectedLabel={selectedLabel}
      enabled={!!workspaceId}
      placeholder={placeholder}
      searchPlaceholder="Search agents…"
      emptyMessage={emptyMessage}
      clearLabel={clearLabel}
      id={id}
      ariaLabel={ariaLabel}
      disabled={disabled}
      className={className}
    />
  );
}
